import React from 'react';
import { XCircle } from 'lucide-react';
import type { Theme } from '../../types';
import { resolveThemeToken } from '../../utils/theme';
import { isValidImageUrl } from '../../utils/fileValidation';
import ExportMenu from './ExportMenu';
import ViewTabs from '../ViewTabs';

interface HeaderProps {
  view: string;
  viewCounts: Record<string, number>;
  theme: Theme;
  companyName: string;
  companyLogo: string;
  agentName: string;
  agentAvatar: string;
  titleId?: string;
  tabsIdBase: string;
  showViewTabs: boolean;
  showSessionsButton?: boolean;
  onSessionsClick?: () => void;
  onViewChange: (view: string) => void;
  onMinimize: () => void;
  onExport: (format: string) => void;
}

const Header = React.memo(function Header({
  view,
  viewCounts,
  companyName,
  companyLogo,
  agentName,
  agentAvatar,
  theme,
  titleId,
  tabsIdBase,
  showViewTabs,
  showSessionsButton,
  onSessionsClick,
  onViewChange,
  onMinimize,
  onExport,
}: HeaderProps) {
  const headerBackground = resolveThemeToken(theme?.header || 'bg-white', 'backgroundColor');
  return (
    <div
      className={`border-b ${headerBackground.className}`}
      style={headerBackground.style}
      role="banner"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {agentAvatar && isValidImageUrl(agentAvatar) && (
            <img
              src={agentAvatar}
              alt={`${agentName} avatar`}
              className="w-8 h-8 rounded-full border border-gray-200 object-cover"
              loading="lazy"
              decoding="async"
              onError={e => {
                (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
          )}
          <div className="leading-tight text-gray-900">
            <h2 className="text-sm font-semibold" id={titleId}>
              {companyLogo && isValidImageUrl(companyLogo) ? (
                <>
                  <img
                    src={companyLogo}
                    alt={`${companyName} logo`}
                    className="h-3 w-auto max-w-[90px] object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={e => {
                      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                  <span className="sr-only">{agentName}</span>
                </>
              ) : (
                agentName
              )}
            </h2>
            <div className="text-xs flex items-center gap-1 opacity-90">
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  background: '#22c55e',
                  borderRadius: '50%',
                }}
                aria-hidden="true"
              />
              Online &middot; {companyName}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showSessionsButton && (
            <button
              onClick={onSessionsClick}
              type="button"
              className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
              title="Sessions"
              aria-label="Sessions"
            >
              <svg
                className="w-4 h-4 text-gray-900"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M3 12a9 9 0 1 0 9-9" />
                <path d="M3 4v4h4" />
                <path d="M12 7v5l4 2" />
              </svg>
            </button>
          )}
          <ExportMenu onExport={onExport} />
          <button
            onClick={onMinimize}
            type="button"
            className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
            title="Minimize"
            aria-label="Minimize chat"
          >
            <XCircle className="w-4 h-4 text-gray-900" aria-hidden="true" />
          </button>
        </div>
      </div>
      {showViewTabs ? (
        <ViewTabs view={view} viewCounts={viewCounts} onChange={onViewChange} idBase={tabsIdBase} />
      ) : null}
    </div>
  );
});

export default Header;
